import { useState, useCallback, useEffect } from 'react';

/**
 * useLocalStorageState behaves like useState but persists data to localStorage.
 *
 * It offers the ability to rerender when other pieces of the page, or other
 * tabs, change the state in local storage by passing `subscribe: true` as an option.
 */
function useLocalStorageState(key, defaultValue, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.subscribe, subscribe = _c === void 0 ? false : _c, _d = _b.serialize, serialize = _d === void 0 ? JSON.stringify : _d, _e = _b.deserialize, deserialize = _e === void 0 ? JSON.parse : _e;
    var _f = useState(function () {
        var valueInLocalStorage = window.localStorage.getItem(key);
        if (valueInLocalStorage) {
            return deserialize(valueInLocalStorage);
        }
        var value = defaultValue instanceof Function ? defaultValue() : defaultValue;
        value !== undefined && window.localStorage.setItem(key, serialize(value));
        return value;
    }), state = _f[0], setState = _f[1];
    var setAndSave = useCallback(function (value) {
        setState(value);
        window.localStorage.setItem(key, serialize(value));
        window.dispatchEvent(new StorageEvent("storage", { key: key, newValue: serialize(value) }));
    }, [key, serialize]);
    useEffect(function () {
        if (!subscribe)
            return;
        var handleChanges = function (storageEvent) {
            var _a;
            if (storageEvent.key === key) {
                var newState = deserialize((_a = storageEvent.newValue) !== null && _a !== void 0 ? _a : "");
                setState(newState);
            }
        };
        window.addEventListener("storage", handleChanges);
        return function () {
            window.removeEventListener("storage", handleChanges);
        };
    }, [key, deserialize, subscribe, setState]);
    return [state, setAndSave];
}

export { useLocalStorageState };
//# sourceMappingURL=useLocalStorageState.esm.js.map
